import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Lock, User as UserIcon } from "lucide-react";
import { apiLogin, apiRegister, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function GoogleButton({ disabled }) {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid="google-signin-btn"
      className="w-full inline-flex items-center justify-center gap-2.5 border border-neutral-800 bg-neutral-950 hover:bg-neutral-900 text-white font-medium text-sm px-6 py-3 transition-colors disabled:opacity-50"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#EA4335" d="M5.27 9.76A7.08 7.08 0 0 1 4.91 12c0 .78.13 1.54.36 2.24L1.28 17A11.97 11.97 0 0 1 0 12c0-1.77.39-3.45 1.08-4.96l4.19 2.72Z"/>
        <path fill="#FBBC05" d="M5.27 9.76 1.08 7.04A11.97 11.97 0 0 1 12 0c3.24 0 6.12 1.16 8.39 3.05l-3.61 3.55A7.04 7.04 0 0 0 12 4.91a7.1 7.1 0 0 0-6.73 4.85Z" transform="rotate(0)"/>
        <path fill="#34A853" d="M12 19.09c2.05 0 3.77-.67 5.03-1.84l3.5 2.72A11.95 11.95 0 0 1 12 24c-4.66 0-8.7-2.66-10.72-6.55l4.19-2.72A7.1 7.1 0 0 0 12 19.09Z"/>
        <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.46a5.52 5.52 0 0 1-2.41 3.74l3.5 2.72c2.04-1.88 3.94-4.65 3.94-8.7Z"/>
      </svg>
      Continue with Google
    </button>
  );
}

function Divider() {
  return (
    <div className="my-5 flex items-center gap-3">
      <div className="flex-1 h-px bg-neutral-900" />
      <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-600">or</span>
      <div className="flex-1 h-px bg-neutral-900" />
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const after = new URLSearchParams(location.search).get("after") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await apiLogin({ email, password });
      await refresh();
      toast.success("Welcome back");
      navigate(after, { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Log in" subtitle="Welcome back to ChartLenz.">
      <GoogleButton disabled={busy} />
      <Divider />
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <Field icon={<Mail className="w-4 h-4" />} placeholder="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
        <Field icon={<Lock className="w-4 h-4" />} placeholder="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
        {err && <div className="text-xs text-red-400" data-testid="login-error">{err}</div>}
        <button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors disabled:opacity-50">
          {busy ? "Logging in…" : <>Log in <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
      <div className="mt-3 text-xs text-center">
        <Link to="/forgot-password" data-testid="goto-forgot" className="text-neutral-500 hover:text-white">
          forgot password?
        </Link>
      </div>
      <div className="mt-2 text-xs text-neutral-500 text-center">
        No account? <Link to={`/signup${after ? `?after=${encodeURIComponent(after)}` : ""}`} data-testid="goto-signup" className="text-white hover:underline">Sign up</Link>
      </div>
    </AuthShell>
  );
}

export function SignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const after = new URLSearchParams(location.search).get("after") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await apiRegister({ email, password, name });
      await refresh();
      toast.success("Account created — please verify your email");
      navigate("/verify-email", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };
  const duplicate = /already registered/i.test(err);

  return (
    <AuthShell title="Create account" subtitle="Start with a 1-minute signup.">
      <GoogleButton disabled={busy} />
      <Divider />
      <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
        <Field icon={<UserIcon className="w-4 h-4" />} placeholder="Name (optional)" type="text" value={name} onChange={setName} testid="signup-name" />
        <Field icon={<Mail className="w-4 h-4" />} placeholder="Email" type="email" value={email} onChange={setEmail} testid="signup-email" required />
        <Field icon={<Lock className="w-4 h-4" />} placeholder="Password (min 6 chars)" type="password" value={password} onChange={setPassword} testid="signup-password" required />
        {err && (
          <div className="text-xs text-red-400" data-testid="signup-error">
            {duplicate ? (
              <>An account with this email already exists. <Link to="/login" data-testid="signup-duplicate-login" className="text-white underline">Log in</Link> or <Link to="/forgot-password" data-testid="signup-duplicate-forgot" className="text-white underline">reset your password</Link>.</>
            ) : err}
          </div>
        )}
        <button type="submit" disabled={busy} data-testid="signup-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors disabled:opacity-50">
          {busy ? "Creating…" : <>Create account <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
      <div className="mt-5 text-xs text-neutral-500 text-center">
        Already have an account? <Link to={`/login${after ? `?after=${encodeURIComponent(after)}` : ""}`} data-testid="goto-login" className="text-white hover:underline">Log in</Link>
      </div>
    </AuthShell>
  );
}

function Field({ icon, placeholder, type, value, onChange, testid, required }) {
  return (
    <label className="flex items-center gap-2 border border-neutral-800 bg-neutral-950 px-3.5 focus-within:border-neutral-600 transition-colors">
      <span className="text-neutral-500">{icon}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="flex-1 bg-transparent border-0 outline-none py-3 text-sm text-white placeholder:text-neutral-600"
        autoComplete={type === "password" ? "current-password" : "email"}
      />
    </label>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <section className="pt-16 pb-20 max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="border border-neutral-900 bg-neutral-950/60 p-7"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
          ── chartlenz ──
        </div>
        <h1 className="text-3xl font-black tracking-tight text-white">{title}</h1>
        <p className="text-sm text-neutral-400 mt-1.5 mb-6">{subtitle}</p>
        {children}
      </motion.div>
    </section>
  );
}
